import { useStudioStream } from "@/data/studio-react/useStudioStream";
import { CellClickedEventArgs, Item } from "@glideapps/glide-data-grid";
import {
  ArrowDownIcon,
  DocumentDuplicateIcon,
  PencilSquareIcon,
  PlusIcon,
  Square3Stack3DIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { classNames } from "../../data/classnames";
import { Lot } from "../../types";

// Popper.js
import { ShoppingCartIcon } from "@heroicons/react/24/outline";
import { usePopper } from "react-popper";

export function useDataGridContextMenu(
  onEdit?: (cell: Item) => void,
  onDuplicate?: (cell: Item) => void,
  onInsertAbove?: (cell: Item) => void,
  onInsertBelow?: (cell: Item) => void,
  onDelete?: (cell: Item) => void,
  onGroup?: () => void,
  onUngroup?: (shouldShowCheck?: boolean) => void,
  onRemoveFromGroup?: (shouldShowCheck?: boolean) => boolean,
  onFastFillEdit?: (cell: Item) => void,
  onInsertEnd?: () => void,
  filteredLots?: Lot[],
  selectedRows?: Set<number>,
  gridSelection?: any,
  onCheckout?: (
    customerId: string,
    customerName: string,
    type: "buyer" | "seller"
  ) => void
) {
  let [cell, setCell] = useState<Item>();
  let [bounds, setBounds] = useState<any>(null);
  const virtualReference = useMemo(
    () => ({
      getBoundingClientRect() {
        return {
          left: bounds?.x || 0,
          top: bounds?.y || 0,
          width: bounds?.width || 0,
          height: bounds?.height || 0,
          right: (bounds?.x || 0) + (bounds?.width || 0),
          bottom: (bounds?.y || 0) + (bounds?.height || 0),
          x: bounds?.x || 0,
          y: bounds?.y || 0,
        };
      },
    }),
    [bounds]
  );

  let onCellContextMenu = useCallback(
    (cell: Item, event: CellClickedEventArgs) => {
      event.preventDefault();
      setBounds(event.bounds);
      setCell(cell);
    },
    []
  );

  // Close the menu on page resize or click elsewhere
  let close = useCallback(() => {
    //   setBounds(undefined); // TODO Need to fix this
    setCell(undefined);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      close();
    };
    const handleClick = () => {
      close();
    };

    // On click outside of the menu, close it
    window.addEventListener("click", handleClick);

    // Add event listeners when the component mounts
    window.addEventListener("resize", handleResize);

    // Remove event listeners when the component unmounts
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("click", handleClick);
    };
  }, [close]);

  let filters = useStudioStream("sale:lotsFiltersAndStats");

  // Checkout logic: determine if we should show checkout option
  const checkoutInfo = useMemo(() => {
    if (
      !filteredLots ||
      !onCheckout ||
      (!selectedRows?.size && !gridSelection?.current)
    ) {
      return null;
    }

    let selectedRowIndices: number[] = [];

    // Get selected row indices
    if (selectedRows && selectedRows.size > 0) {
      selectedRowIndices = Array.from(selectedRows);
    } else if (gridSelection?.current?.cell) {
      selectedRowIndices = [gridSelection.current.cell[1]];
    }

    // If no rows selected, don't show checkout option
    if (selectedRowIndices.length === 0) {
      return null;
    }

    const selectedLots = selectedRowIndices
      .map((index) => filteredLots[index])
      .filter((lot) => lot && !lot.void); // Filter out void lots

    if (selectedLots.length === 0) {
      return null;
    }

    // Check if ALL selected lots have total values - if any don't, don't show checkout options
    const allLotsHaveTotalValue = selectedLots.every(
      (lot) => lot.generated?.totalValueInCents
    );
    if (!allLotsHaveTotalValue) {
      return null;
    }

    const options = [];

    // For single lot selection, show both buyer and seller if they exist
    if (selectedLots.length === 1) {
      const lot = selectedLots[0];
      // Show buyer checkout only if not already invoiced for buyer
      if (lot.buyerCustomerId && lot.buyer?.displayName && !lot.buyerInvoiceId) {
        options.push({
          type: "buyer" as const,
          customerId: lot.buyerCustomerId,
          customerName: lot.buyer.displayName,
          lotCount: 1,
        });
      }
      // Show seller checkout only if not already invoiced for seller
      if (lot.sellerCustomerId && lot.seller?.displayName && !lot.sellerInvoiceId) {
        options.push({
          type: "seller" as const,
          customerId: lot.sellerCustomerId,
          customerName: lot.seller.displayName,
          lotCount: 1,
        });
      }
      return options;
    }

    // For multiple lots, check if all have same buyer or seller
    // Note: selectedLots already filtered to only include lots with totalValueInCents
    
    // Filter lots for buyer checkout (not already invoiced for buyer)
    const buyerEligibleLots = selectedLots.filter(lot => lot.buyerCustomerId && !lot.buyerInvoiceId);
    const firstBuyerId = buyerEligibleLots[0]?.buyerCustomerId;
    const firstBuyerName = buyerEligibleLots[0]?.buyer?.displayName;
    const allSameBuyer =
      firstBuyerId &&
      buyerEligibleLots.length === selectedLots.length && // All selected lots are eligible
      buyerEligibleLots.every((lot) => lot.buyerCustomerId === firstBuyerId);

    // Filter lots for seller checkout (not already invoiced for seller)
    const sellerEligibleLots = selectedLots.filter(lot => lot.sellerCustomerId && !lot.sellerInvoiceId);
    const firstSellerId = sellerEligibleLots[0]?.sellerCustomerId;
    const firstSellerName = sellerEligibleLots[0]?.seller?.displayName;
    const allSameSeller =
      firstSellerId &&
      sellerEligibleLots.length === selectedLots.length && // All selected lots are eligible
      sellerEligibleLots.every((lot) => lot.sellerCustomerId === firstSellerId);

    if (allSameBuyer && firstBuyerId && firstBuyerName) {
      options.push({
        type: "buyer" as const,
        customerId: firstBuyerId,
        customerName: firstBuyerName,
        lotCount: buyerEligibleLots.length,
      });
    }

    if (allSameSeller && firstSellerId && firstSellerName) {
      options.push({
        type: "seller" as const,
        customerId: firstSellerId,
        customerName: firstSellerName,
        lotCount: sellerEligibleLots.length,
      });
    }

    return options.length > 0 ? options : null;
  }, [filteredLots, selectedRows, gridSelection, onCheckout]);

  let sections = useMemo(() => {
    return [
      {
        buttons: [
          {
            className: "",
            show: !!onFastFillEdit,
            icon: (
              <PencilSquareIcon
                className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                aria-hidden="true"
              />
            ),
            text: "Edit in Builder",
            onClick: () => {
              if (onFastFillEdit && cell) {
                onFastFillEdit(cell);
              }
              close();
            },
          },
          {
            className: "",
            show: !!onGroup,
            icon: (
              <Square3Stack3DIcon
                className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                aria-hidden="true"
              />
            ),
            text: "Group Lots",
            onClick: () => {
              if (onGroup) {
                onGroup();
              }
              close();
            },
          },

          {
            className: "",
            show: onRemoveFromGroup && onRemoveFromGroup(true),
            icon: (
              <Square3Stack3DIcon
                className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                aria-hidden="true"
              />
            ),
            text: "Remove From Group",
            onClick: () => {
              if (onRemoveFromGroup) {
                onRemoveFromGroup();
              }
              close();
            },
          },

          {
            className: "",
            show: onUngroup && onUngroup(true),
            icon: (
              <Square3Stack3DIcon
                className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                aria-hidden="true"
              />
            ),
            text: "Ungroup All",
            onClick: () => {
              if (onUngroup) {
                onUngroup();
              }
              close();
            },
          },

          {
            className: "",
            show: !!onDuplicate,
            icon: (
              <DocumentDuplicateIcon
                className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                aria-hidden="true"
              />
            ),
            text: "Duplicate Row",
            onClick: () => {
              if (onDuplicate && cell) {
                onDuplicate(cell);
              }
              close();
            },
          },
        ],
      },
      // Checkout section - separate section for visual separation
      ...(checkoutInfo && checkoutInfo.length > 0
        ? [
            {
              buttons: checkoutInfo.map((option) => ({
                className: "text-left",
                show: true,
                icon: (
                  <ShoppingCartIcon
                    className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                    aria-hidden="true"
                  />
                ),
                text: option.customerName,
                lotCount: option.lotCount,
                onClick: () => {
                  if (onCheckout) {
                    onCheckout(
                      option.customerId,
                      option.customerName,
                      option.type
                    );
                  }
                  close();
                },
              })),
            },
          ]
        : []),
      {
        buttons: [
          {
            className: "",
            show: !!onInsertBelow,
            icon: (
              <ArrowDownIcon
                className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                aria-hidden="true"
              />
            ),
            text: "Insert Row Below",
            onClick: () => {
              if (onInsertBelow && cell) {
                onInsertBelow(cell);
              }
              close();
            },
          },
          {
            className: "",
            show: !!onInsertEnd,
            icon: (
              <PlusIcon
                className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 rotate-90"
                aria-hidden="true"
              />
            ),
            text: "Insert Lots",
            onClick: () => {
              if (onInsertEnd) {
                onInsertEnd();
              }
              close();
            },
          },
        ],
      },
      {
        buttons: [
          {
            className: "!text-danger-400 !group-hover:text-red-500",
            show: !!onDelete,
            icon: (
              <TrashIcon
                className="mr-3 h-5 w-5 text-danger-400 group-hover:text-danger-500"
                aria-hidden="true"
              />
            ),

            text: "Delete Row",
            onClick: () => {
              if (onDelete && cell) {
                onDelete(cell);
              }
              close();
            },
          },
        ],
      },
    ];
  }, [
    onEdit,
    onDuplicate,
    onInsertAbove,
    onInsertBelow,
    onDelete,
    cell,
    close,
    onGroup,
    onUngroup,
    onRemoveFromGroup,
    onFastFillEdit,
    filters,
    onInsertEnd,
    checkoutInfo,
    onCheckout,
  ]);

  // Has the popup menu got any buttons to show?
  let hasButtons = useMemo(() => {
    return sections.some((section) => section.buttons.some((b) => b.show));
  }, [sections]);

  // Return the Menu Component
  const MenuComponent = () => {
    let [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
      null
    );
    // @ts-ignore error with virtualReference but not sure why – no errors are defined
    let { styles, attributes } = usePopper(virtualReference, popperElement, {
      placement: "bottom-start",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 5],
          },
        },
        {
          name: "eventListeners",
          options: {
            scroll: true,
            resize: true,
          },
        },
      ],
    });

    if (!cell) return null;
    if (!hasButtons) return null;
    return (
      // <Portal>
      <div
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
        className="z-[99999]"
      >
        <div className="relative z-50 w-72 text-left">
          <div className="divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {sections.map((section, index) => {
              let buttonSections = section.buttons.filter((b) => b.show);
              if (buttonSections.length === 0) return null;

              return (
                <ul className="py-1" key={index}>
                  {buttonSections.map((button, index) => {
                    return (
                      <li key={index}>
                        <button
                          type="button"
                          className={classNames(
                            "text-gray-700",
                            "group flex w-full items-center px-4 py-2 text-sm whitespace-nowrap",
                            "hover:bg-gray-100 hover:text-gray-900",
                            button.className
                          )}
                          onClick={button.onClick}
                        >
                          {button.icon}
                          <div className="flex items-center justify-between w-full">
                            <span>{button.text}</span>
                            {"lotCount" in button ? (
                              <span className="ml-2 px-2 py-0.5 text-xs bg-white group-hover:bg-gray-100 text-gray-600 rounded border border-gray-200">
                                {`${button.lotCount} ${button.lotCount === 1 ? 'lot' : 'lots'}`}
                              </span>
                            ) : null}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              );
            })}
          </div>
        </div>
      </div>
      // </Portal>
    );
  };

  return useMemo(() => {
    return {
      MenuComponent,
      onCellContextMenu,
    };
  }, [
    onCellContextMenu,
    MenuComponent,
    virtualReference,
    sections,
    hasButtons,
    cell,
  ]);
}
